// ---------------------------------------------------------------------------
// IDE v2 Slice — feature flag for new concept2 IDE shell (Sub-project B PR1)
//
// ideV2Enabled defaults to TRUE since Sub-project B PR6 (Sub-project B closure).
// Opt-out via ?ide=v1 query param OR setIdeV2Enabled(false) at runtime.
// ---------------------------------------------------------------------------

import type { ChannelOutput } from '../features/_shared/channel-types'
import type { ChatMessage } from '../features/channel-router/chat-message'
import type { OpenPencilFigmaSourceBundle } from '../features/openpencil-figma/openpencil-figma-source'
import type { OpenPencilImportActivity } from '../features/openpencil-figma/openpencil-import-activity'
import {
  createOpenPencilSourceWorkspace,
  type OpenPencilSourceWorkspace,
} from '../features/openpencil-figma/openpencil-source-workspace'
import type { ProjectDesignSystemRegistrationSummary } from '../features/design-system/lib/register-project-design-system'
import type { VocabularyEnumKey } from '../features/design-system/vocabulary/types'
import { CREATOR_RAIL_ITEMS } from '../shell/ide-v2/types'
import { INITIAL_ACTIVE_RAIL_ITEM } from '../shell/ide-v2/rail-defaults'
import { assertNoOpenPencilDesignMutationActive } from '../features/openpencil-figma/openpencil-design-mutation-guard'

/**
 * What the workspace's one request slot is doing.
 *
 * `request-in-flight` is the CHAT composer's phase. The other running phases
 * name stages of the channel router's pipeline, which is a lane
 * `StudioComposer` does not take: it sends the message to the agent runtime,
 * which may answer in prose, with a canned surface, or by generating an
 * application. Borrowing `real-generation` for that would have made every
 * question look like a build. Nothing consumes this union exhaustively — the
 * readers are `if` chains — so a reader that has no opinion about it keeps its
 * existing default.
 */
export type ChannelDispatchPhase = 'idle' | 'request-in-flight' | 'pattern-retrieval' | 'mock-generation' | 'real-generation' | 'rendering' | 'completed' | 'failed' | 'aborted'

export interface ChannelDispatchStatus {
  phase: ChannelDispatchPhase
  mode: 'mock' | 'real' | null
  startedAt: number | null
  updatedAt: number | null
  message: string | null
}

export const IDLE_CHANNEL_DISPATCH_STATUS: ChannelDispatchStatus = {
  phase: 'idle',
  mode: null,
  startedAt: null,
  updatedAt: null,
  message: null,
}

const LOCAL_STORAGE_KEY_INSPECTOR_POSITION = 'studio.inspectorPosition'
const LOCAL_STORAGE_KEY_DEVTOOLS_DOCK = 'studio.devtoolsDock'
const LOCAL_STORAGE_KEY_STUDIO_MODE = 'studio.studioMode'
const LOCAL_STORAGE_KEY_STUDIO_PERSONA = 'studio.persona'
const LOCAL_STORAGE_KEY_ONBOARDING = 'studio.onboarding.v90'
// Phase A P2 — Channel 2 real-backend opt-in (off by default; persisted)
const LOCAL_STORAGE_KEY_USE_REAL_CHANNEL_2 = 'studio.useRealChannel2'
const LOCAL_STORAGE_KEY_COGO_BACKEND_URL = 'studio.cogoBackendUrl'

/**
 * Where block generation is called, by default: THIS origin.
 *
 * The host used to be baked (`https://api.cogo.xyz/api/v3/block/generate`) and
 * it carried no information the serving origin did not already carry. Measured
 * 2026-08-26, same body, byte for byte:
 *
 *   POST https://api.cogo.xyz/api/v3/block/generate  400
 *     {"error":"Missing or invalid 'blockSpec' — must contain: name, type, description"}
 *   POST https://www.cogo.xyz/api/v3/block/generate  400
 *     {"error":"Missing or invalid 'blockSpec' — must contain: name, type, description"}
 *
 * and the control, so the second 400 is known to be routing and not a catch-all:
 *
 *   POST https://www.cogo.xyz/api/v3/block/not-a-route 404 {"error":"Unknown v3 endpoint"}
 *
 * On cloud the relative path therefore changes nothing. On an appliance it is
 * the difference between failing locally with no egress and dialling a host the
 * network cannot reach. `VITE_COGO_BACKEND_URL` still overrides it at build
 * time, and `studio.cogoBackendUrl` in localStorage still overrides it at
 * runtime — the unusual case stays nameable; it just stops being the default.
 */
export const DEFAULT_COGO_BACKEND_URL = (import.meta.env as Record<string, string | undefined>).VITE_COGO_BACKEND_URL ?? '/api/v3/block/generate'

/**
 * StudioMode a `/studio?template=` deeplink should land in.
 *
 * Default `'edit'` (Phase 039-02 D-039-4b): render the loaded template
 * instead of hiding it under the chat `EmptyState` (the IDEShellLayout
 * `isEmpty` gate would otherwise cover it at the default `'build'` mode).
 *
 * `&mode=build` opts back into the chat-first build entry — EmptyState over
 * the loaded workspace — for chat-driven starts and real-mode dogfooding
 * (2026-06-11: previously the flip was unconditional, so build-mode entry
 * via a template deeplink was impossible without store surgery).
 */
export function resolveTemplateDeeplinkMode(params: URLSearchParams): 'build' | 'edit' {
  return params.get('mode') === 'build' ? 'build' : 'edit'
}

// A3.4 — refinement chip data (element click → ChatRail input)
export interface ElementChip {
  element_id: string
  element_type: string
  block_id: string
  label: string
}

export type StudioPersona = 'creator' | 'developer'
export type StudioOnboardingStatus = 'pending' | 'completed' | 'skipped'
export type WorkingPageView = 'app' | 'report'
export type WorkingReportKind = 'spec-ontology' | 'gap-test'

export interface FigmaSourceCredentialSession {
  workspaceId: string
  tokenRef: string
  tokenLabel?: string
  confirmedAt: number
}

export interface IdeV2Slice {
  // ── Rail navigation ──
  activeRailItem: import('../shell/ide-v2/types').RailItemId
  setActiveRailItem: (id: import('../shell/ide-v2/types').RailItemId) => void
  reviewedCockpitActivityIds: string[]
  markCockpitActivityReviewed: (id: string) => void
  markCockpitActivitiesReviewed: (ids: string[]) => void

  // ── Contextual resources ──
  resourcesSelection: import('../shell/ide-v2/selection-context').IdeSelectionContext | null
  setResourcesSelection: (selection: import('../shell/ide-v2/selection-context').IdeSelectionContext | null) => void
  resourcesDrawerOpen: boolean
  openResourcesDrawer: (selection: import('../shell/ide-v2/selection-context').IdeSelectionContext | null) => void
  closeResourcesDrawer: () => void


  // ── v2.2 Phase 029 — Ontology panel deep-link selection (replaces DsSurface local useState) ──
  ontologySelectedIri: string | null
  setOntologySelectedIri: (iri: string | null) => void

  // ── Phase E — Vocabulary sub-tab active enum ──
  vocabularyActiveEnum: VocabularyEnumKey
  setVocabularyActiveEnum: (key: VocabularyEnumKey) => void

  // ── Phase A — studio mode (build/edit toggle) ──
  studioMode: import('../shell/ide-v2/types').StudioMode
  setStudioMode: (mode: import('../shell/ide-v2/types').StudioMode) => void
  _hydrateStudioMode: () => void

  // ── Phase 534 — onboarding + persona switch ──
  studioPersona: StudioPersona
  setStudioPersona: (persona: StudioPersona) => void
  _hydrateStudioPersona: () => void
  onboardingStatus: StudioOnboardingStatus
  completeOnboarding: () => void
  skipOnboarding: () => void
  resetOnboarding: () => void
  _hydrateOnboarding: () => void

  // ── A3.3 — channel router state ──
  dispatchedOutput: ChannelOutput | null
  chatMessages: ChatMessage[]
  /** Unsent composer text survives responsive panel unmounts and workspace switches. */
  chatComposerDraft: string
  pageProgressFrame: ChatMessage | null
  channelDispatchStatus: ChannelDispatchStatus
  setDispatchedOutput: (out: ChannelOutput | null) => void
  appendChatMessage: (msg: ChatMessage) => void
  setChatComposerDraft: (draft: string) => void
  replacePageProgress: (msg: ChatMessage | null) => void
  setChannelDispatchStatus: (status: ChannelDispatchStatus) => void
  clearDispatch: () => void

  // ── Phase A P3 — dispatch reset signal + project load error ──
  /** Increments on every clearDispatch. EmptyState consumes via useEffect to reset local input state (#15). */
  dispatchResetSignal: number
  /** Error message from a failed openServerProject. UI renders inline error when truthy (#1). */
  projectLoadError: string | null
  /** Sets projectLoadError. null clears the error. */
  setProjectLoadError: (msg: string | null) => void

  // ── A3.4 — refinement chip ──
  chatRailChip: ElementChip | null
  setChatRailChip: (c: ElementChip | null) => void
  clearChatRailChip: () => void

  // ── Figma source credential session ──
  // Stores only the non-secret server-side credential reference. The actual
  // Figma token remains in server storage and is never persisted in browser UI
  // state.
  figmaSourceCredential: FigmaSourceCredentialSession | null
  setFigmaSourceCredential: (credential: FigmaSourceCredentialSession) => void
  clearFigmaSourceCredential: () => void

  // ── OpenPencil .fig source sidecar ──
  openPencilFigmaSource: OpenPencilFigmaSourceBundle | null
  openPencilSourceWorkspace: OpenPencilSourceWorkspace | null
  /**
   * The project-scoped design system derived from the imported .fig
   * (`settings.projectDesignSystem`, written by POST /api/design-system/
   * project-registration). Hydrated on project load and refreshed when the
   * user registers; generation binds it through
   * `resolveProjectDesignSystemGenerationBinding`, which re-checks it against
   * the LIVE workspace, so a stale value here can only fail closed.
   */
  projectDesignSystemRegistration: ProjectDesignSystemRegistrationSummary | null
  setOpenPencilFigmaSource: (bundle: OpenPencilFigmaSourceBundle) => void
  setOpenPencilSourceWorkspace: (workspace: OpenPencilSourceWorkspace) => void
  setProjectDesignSystemRegistration: (
    registration: ProjectDesignSystemRegistrationSummary | null,
  ) => void
  clearOpenPencilFigmaSource: () => void

  // ── Source (.fig) import journey (UX audit 2026-07-27) ──
  // Shared so the import surface, the source workspace canvas, and the
  // cockpit/Status attention projection all describe the SAME import.
  openPencilImportActivity: OpenPencilImportActivity | null
  setOpenPencilImportActivity: (
    activity: OpenPencilImportActivity | null,
  ) => void

  // ── Phase A P2 — Channel 2 real backend opt-in ──
  // useRealChannel2: when true, /studio main route's useChannelDispatch
  //   threads `realMode: true` + `backendUrl` into routeChannel → Channel 2
  //   calls cogo-cloudflare /api/v3/block/generate instead of the mock
  //   pipeline. Default false. Persisted to localStorage.
  // cogoBackendUrl: full URL of /api/v3/block/generate. Defaults to
  //   VITE_COGO_BACKEND_URL build-time env or https://api.cogo.xyz/...
  // realPipeline: 'v2' (default — bypasses ontology gate for Phase 1) or
  //   'hybrid-with-fallback'. Stored in-memory only (rarely changes).
  useRealChannel2: boolean
  cogoBackendUrl: string
  realPipeline: 'v2' | 'hybrid-with-fallback'
  setUseRealChannel2: (enabled: boolean) => void
  setCogoBackendUrl: (url: string) => void
  setRealPipeline: (p: 'v2' | 'hybrid-with-fallback') => void
  _hydrateRealChannel2FromStorage: () => void

  // ── Project context (stubs — downstream PRs wire real values) ──
  currentProjectName?: string
  currentProjectSlug?: string
  exitProject?: () => void

  // ── Working page surface ──
  workingPageView: WorkingPageView
  workingReportKind: WorkingReportKind
  setWorkingPageView: (view: WorkingPageView) => void
  setWorkingReportKind: (kind: WorkingReportKind) => void
  openWorkingReport: (kind: WorkingReportKind) => void

  // ── Viewport preview (PR3) ──
  viewportPreview: 'mobile' | 'tablet' | 'desktop'
  setViewportPreview: (vp: 'mobile' | 'tablet' | 'desktop') => void

  // ── Undo / Redo stubs (PR3 — wired to redux in later integration phase) ──
  undo?: () => void
  redo?: () => void

  // ── Inspector overlay (PR4) ──
  inspectorOverlayOpen: boolean
  activeComponentId: string | null
  inspectorPosition: { x: number; y: number }
  // Phase 050 D-050 PATTERNS Cross-cutting (a) Path 1 — discriminator
  // identifying which body the overlay renders. 'abox' = ABox properties
  // panel (Phase 050); 'metadata' = PR4 PropertyMetadataRegistry-driven
  // panel (existing); null = closed.
  inspectorOverlayContent: 'abox' | 'metadata' | null
  selectComponent: (id: string) => void
  closeInspector: () => void
  // Phase 050 D-050 — open the inspector overlay with the ABox properties
  // panel body. Sets inspectorOverlayContent='abox' + inspectorOverlayOpen=true
  // + activeComponentId=null (the ABox panel reads previewSelection from the
  // preview-selection-slice; activeComponentId is the PR4 path's identifier).
  openAboxPropertiesPanel: () => void
  setInspectorPosition: (pos: { x: number; y: number }) => void
  _hydrateInspectorPosition: () => void

  // ── DevTools dock (PR5) ──
  devtoolsDockOpen: boolean
  devtoolsDockHeight: number
  toggleDevtoolsDock: () => void
  setDevtoolsDockHeight: (h: number) => void
  _hydrateDevtoolsDock: () => void

  // ── Plan B2 Task 4 — template content-form apply gate ──
  // True while `applySlotValues` (features/templates/content-form) is
  // mid-flight: validate → saveBlock/saveProjectMeta batch → clear. Task 5's
  // divergedAt marking checks this exact flag name to distinguish "an
  // in-progress content-form apply" from any other edit path that might also
  // touch the same blocks.
  templateFormApplyInFlight: boolean
  setTemplateFormApplyInFlight: (inFlight: boolean) => void
}

type Set_ = (fn: (state: IdeV2Slice) => void) => void

export function createIdeV2Slice(set: Set_): IdeV2Slice {
  return {
    // ── Rail navigation ──
    // Shared with the project-exit reset — see `rail-defaults.ts`.
    activeRailItem: INITIAL_ACTIVE_RAIL_ITEM,
    setActiveRailItem: (id) =>
      set((s) => {
        s.activeRailItem = id
      }),
    reviewedCockpitActivityIds: [],
    markCockpitActivityReviewed: (id) =>
      set((s) => {
        s.reviewedCockpitActivityIds = [
          ...new Set([...s.reviewedCockpitActivityIds, id]),
        ]
      }),
    markCockpitActivitiesReviewed: (ids) =>
      set((s) => {
        s.reviewedCockpitActivityIds = [
          ...new Set([...s.reviewedCockpitActivityIds, ...ids]),
        ]
      }),

    // ── Contextual resources ──
    resourcesSelection: null,
    setResourcesSelection: (selection) =>
      set((s) => {
        s.resourcesSelection = selection
      }),
    resourcesDrawerOpen: false,
    openResourcesDrawer: (selection) =>
      set((s) => {
        s.resourcesSelection = selection
        s.resourcesDrawerOpen = true
      }),
    closeResourcesDrawer: () =>
      set((s) => {
        s.resourcesDrawerOpen = false
      }),


    // ── v2.2 Phase 029 — Ontology panel deep-link selection ──
    ontologySelectedIri: null,
    setOntologySelectedIri: (iri) =>
      set((s) => {
        s.ontologySelectedIri = iri
      }),

    // ── Phase E — Vocabulary sub-tab active enum ──
    vocabularyActiveEnum: 'prefix',
    setVocabularyActiveEnum: (key) =>
      set((s) => {
        s.vocabularyActiveEnum = key
      }),

    // ── Phase A — studio mode ──
    studioMode: 'build',

    setStudioMode: (mode) => {
      set((s) => {
        s.studioMode = mode
      })
      try {
        if (mode === 'edit') localStorage.setItem(LOCAL_STORAGE_KEY_STUDIO_MODE, 'edit')
        else localStorage.removeItem(LOCAL_STORAGE_KEY_STUDIO_MODE)
      } catch {
        // ignore localStorage quota / privacy mode failures
      }
    },

    _hydrateStudioMode: () => {
      try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY_STUDIO_MODE)
        if (stored === 'edit' || stored === 'build') {
          set((s) => {
            s.studioMode = stored
          })
        }
      } catch {
        // ignore
      }
    },

    // ── Phase 534 — onboarding + tool visibility switch ──
    studioPersona: 'creator',
    setStudioPersona: (persona) => {
      set((s) => {
        s.studioPersona = persona
        if (persona === 'creator' && !CREATOR_RAIL_ITEMS.has(s.activeRailItem)) {
          s.activeRailItem = 'preview'
        }
      })
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_STUDIO_PERSONA, persona)
      } catch {
        // ignore localStorage quota / privacy mode failures
      }
    },
    _hydrateStudioPersona: () => {
      try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY_STUDIO_PERSONA)
        if (stored === 'creator' || stored === 'developer') {
          set((s) => {
            s.studioPersona = stored
            if (stored === 'creator' && !CREATOR_RAIL_ITEMS.has(s.activeRailItem)) {
              s.activeRailItem = 'preview'
            }
          })
        }
      } catch {
        // ignore
      }
    },
    onboardingStatus: 'pending',
    completeOnboarding: () => {
      set((s) => {
        s.onboardingStatus = 'completed'
      })
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_ONBOARDING, 'completed')
      } catch {
        // ignore
      }
    },
    skipOnboarding: () => {
      set((s) => {
        s.onboardingStatus = 'skipped'
      })
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_ONBOARDING, 'skipped')
      } catch {
        // ignore
      }
    },
    resetOnboarding: () => {
      set((s) => {
        s.onboardingStatus = 'pending'
      })
      try {
        localStorage.removeItem(LOCAL_STORAGE_KEY_ONBOARDING)
      } catch {
        // ignore
      }
    },
    _hydrateOnboarding: () => {
      try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY_ONBOARDING)
        if (stored === 'completed' || stored === 'skipped') {
          set((s) => {
            s.onboardingStatus = stored
          })
        }
      } catch {
        // ignore
      }
    },

    // ── A3.3 — channel router state ──
    dispatchedOutput: null,
    chatMessages: [],
    chatComposerDraft: '',
    pageProgressFrame: null,
    channelDispatchStatus: IDLE_CHANNEL_DISPATCH_STATUS,
    setDispatchedOutput: (out) =>
      set((s) => {
        s.dispatchedOutput = out
      }),
    appendChatMessage: (msg) =>
      set((s) => {
        s.chatMessages.push(msg)
      }),
    setChatComposerDraft: (draft) =>
      set((s) => {
        s.chatComposerDraft = draft
      }),
    replacePageProgress: (msg) =>
      set((s) => {
        s.pageProgressFrame = msg
      }),
    setChannelDispatchStatus: (status) =>
      set((s) => {
        s.channelDispatchStatus = status
      }),
    clearDispatch: () =>
      set((s) => {
        s.dispatchedOutput = null
        s.chatMessages = []
        s.pageProgressFrame = null
        s.channelDispatchStatus = IDLE_CHANNEL_DISPATCH_STATUS
        s.dispatchResetSignal = (s.dispatchResetSignal ?? 0) + 1
        s.projectLoadError = null
        // A chip references element/block ids of the discarded output —
        // keeping it would attach stale refinementContext to the next run.
        s.chatRailChip = null
      }),

    // ── Phase A P3 — dispatch reset signal + project load error ──
    dispatchResetSignal: 0,
    projectLoadError: null,
    setProjectLoadError: (msg) =>
      set((s) => {
        s.projectLoadError = msg
      }),

    // ── A3.4 — refinement chip ──
    chatRailChip: null,
    setChatRailChip: (c) =>
      set((s) => {
        s.chatRailChip = c
      }),
    clearChatRailChip: () =>
      set((s) => {
        s.chatRailChip = null
      }),

    // ── Figma source credential session ──
    figmaSourceCredential: null,
    setFigmaSourceCredential: (credential) =>
      set((s) => {
        s.figmaSourceCredential = credential
      }),
    clearFigmaSourceCredential: () =>
      set((s) => {
        s.figmaSourceCredential = null
      }),

    // ── OpenPencil .fig source sidecar ──
    openPencilFigmaSource: null,
    openPencilSourceWorkspace: null,
    projectDesignSystemRegistration: null,
    setProjectDesignSystemRegistration: (registration) => {
      set((s) => {
        s.projectDesignSystemRegistration = registration
      })
    },
    setOpenPencilFigmaSource: (bundle) => {
      assertNoOpenPencilDesignMutationActive()
      const workspace = createOpenPencilSourceWorkspace(bundle)
      set((s) => {
        s.openPencilFigmaSource = workspace.bundle
        s.openPencilSourceWorkspace = workspace
      })
    },
    setOpenPencilSourceWorkspace: (workspace) => {
      assertNoOpenPencilDesignMutationActive()
      set((s) => {
        s.openPencilSourceWorkspace = workspace
        s.openPencilFigmaSource = workspace.bundle
      })
    },
    clearOpenPencilFigmaSource: () => {
      assertNoOpenPencilDesignMutationActive()
      set((s) => {
        s.openPencilFigmaSource = null
        s.openPencilSourceWorkspace = null
      })
    },

    // ── Source (.fig) import journey ──
    openPencilImportActivity: null,
    setOpenPencilImportActivity: (activity) =>
      set((s) => {
        s.openPencilImportActivity = activity
      }),

    // ── Phase A P2 — Channel 2 real backend opt-in ──
    useRealChannel2: false,
    cogoBackendUrl: DEFAULT_COGO_BACKEND_URL,
    realPipeline: 'v2',
    setUseRealChannel2: (enabled) => {
      set((s) => {
        s.useRealChannel2 = enabled
      })
      try {
        if (enabled) localStorage.setItem(LOCAL_STORAGE_KEY_USE_REAL_CHANNEL_2, 'true')
        else localStorage.removeItem(LOCAL_STORAGE_KEY_USE_REAL_CHANNEL_2)
      } catch {
        /* ignore */
      }
    },
    setCogoBackendUrl: (url) => {
      set((s) => {
        s.cogoBackendUrl = url
      })
      try {
        if (url && url !== DEFAULT_COGO_BACKEND_URL) {
          localStorage.setItem(LOCAL_STORAGE_KEY_COGO_BACKEND_URL, url)
        } else {
          // Stored only when overridden — default is rebuilt from env
          // each session, so we don't persist it.
          localStorage.removeItem(LOCAL_STORAGE_KEY_COGO_BACKEND_URL)
        }
      } catch {
        /* ignore */
      }
    },
    setRealPipeline: (p) =>
      set((s) => {
        s.realPipeline = p
      }),
    // NOTE: must be invoked from App.tsx's mount-hydration effect alongside
    // _hydrateStudioMode — it was defined but never called until 2026-06-11,
    // so the persisted real-mode opt-in silently never restored.
    _hydrateRealChannel2FromStorage: () => {
      try {
        const flag = localStorage.getItem(LOCAL_STORAGE_KEY_USE_REAL_CHANNEL_2)
        if (flag === 'true')
          set((s) => {
            s.useRealChannel2 = true
          })
        const url = localStorage.getItem(LOCAL_STORAGE_KEY_COGO_BACKEND_URL)
        if (url && url.length > 0)
          set((s) => {
            s.cogoBackendUrl = url
          })
      } catch {
        /* ignore */
      }
    },

    // ── Project context (stubs — undefined; downstream PRs wire real values) ──
    currentProjectName: undefined,
    currentProjectSlug: undefined,
    exitProject: () => {
      window.location.href = '/dashboard/projects'
    },

    // ── Working page surface ──
    workingPageView: 'app',
    workingReportKind: 'spec-ontology',
    setWorkingPageView: (view) =>
      set((s) => {
        s.workingPageView = view
      }),
    setWorkingReportKind: (kind) =>
      set((s) => {
        s.workingReportKind = kind
      }),
    openWorkingReport: (kind) =>
      set((s) => {
        s.workingPageView = 'report'
        s.workingReportKind = kind
      }),

    // ── Viewport preview (PR3) ──
    viewportPreview: 'desktop',
    setViewportPreview: (vp) =>
      set((s) => {
        s.viewportPreview = vp
      }),

    // ── Undo / Redo stubs (PR3) ──
    undo: () => {
      // TODO: dispatch existing redux undo action when integration phase wires it
    },
    redo: () => {
      // TODO: dispatch existing redux redo action when integration phase wires it
    },

    // ── Inspector overlay (PR4) ──
    inspectorOverlayOpen: false,
    activeComponentId: null,
    inspectorPosition: { x: 100, y: 100 },
    // Phase 050 D-050 — content discriminator (Cross-cutting (a) Path 1).
    inspectorOverlayContent: null,

    selectComponent: (id) =>
      set((s) => {
        s.activeComponentId = id
        s.inspectorOverlayOpen = true
        // Phase 050 — existing path = PR4 PropertyMetadataRegistry panel.
        s.inspectorOverlayContent = 'metadata'
      }),

    closeInspector: () =>
      set((s) => {
        s.inspectorOverlayOpen = false
        s.activeComponentId = null
        // Phase 050 — clear discriminator on close.
        s.inspectorOverlayContent = null
      }),

    // Phase 050 D-050 — open the inspector overlay with the ABox panel body.
    openAboxPropertiesPanel: () =>
      set((s) => {
        s.inspectorOverlayOpen = true
        s.inspectorOverlayContent = 'abox'
        s.activeComponentId = null
      }),

    setInspectorPosition: (pos) => {
      set((s) => {
        s.inspectorPosition = pos
      })
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_INSPECTOR_POSITION, JSON.stringify(pos))
      } catch {
        // ignore localStorage quota / privacy mode failures
      }
    },

    _hydrateInspectorPosition: () => {
      try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY_INSPECTOR_POSITION)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (Number.isFinite(parsed.x) && Number.isFinite(parsed.y)) {
            set((s) => {
              s.inspectorPosition = parsed
            })
          }
        }
      } catch {
        // ignore JSON parse / localStorage failures
      }
    },

    // ── DevTools dock (PR5) ──
    devtoolsDockOpen: false,
    devtoolsDockHeight: 200,

    toggleDevtoolsDock: () => {
      let nextOpen = false
      let curHeight = 200
      set((s) => {
        s.devtoolsDockOpen = !s.devtoolsDockOpen
        nextOpen = s.devtoolsDockOpen
        curHeight = s.devtoolsDockHeight
      })
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_DEVTOOLS_DOCK, JSON.stringify({ open: nextOpen, height: curHeight }))
      } catch {
        /* ignore localStorage quota / privacy mode failures */
      }
    },

    setDevtoolsDockHeight: (h) => {
      const clamped = Math.max(80, Math.min(600, h))
      let curOpen = false
      set((s) => {
        s.devtoolsDockHeight = clamped
        curOpen = s.devtoolsDockOpen
      })
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_DEVTOOLS_DOCK, JSON.stringify({ open: curOpen, height: clamped }))
      } catch {
        /* ignore */
      }
    },

    _hydrateDevtoolsDock: () => {
      try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY_DEVTOOLS_DOCK)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (typeof parsed.open === 'boolean') {
            set((s) => {
              s.devtoolsDockOpen = parsed.open
            })
          }
          if (typeof parsed.height === 'number' && Number.isFinite(parsed.height)) {
            set((s) => {
              s.devtoolsDockHeight = Math.max(80, Math.min(600, parsed.height))
            })
          }
        }
      } catch {
        /* ignore JSON parse / localStorage failures */
      }
    },

    // ── Plan B2 Task 4 — template content-form apply gate ──
    templateFormApplyInFlight: false,
    setTemplateFormApplyInFlight: (inFlight) =>
      set((s) => {
        s.templateFormApplyInFlight = inFlight
      }),
  }
}
